/**
 * Loads (and caches in Redux) the Sam context payload for an activity:
 * source_kind, subject, optional tutor/occurrence/sequence, prescribed_by.
 * The 5-minute window is local-only — see SamContextController for
 * the API contract; the back-end doesn't set Cache-Control so we
 * decide here when it's worth re-fetching.
 *
 * Déplacé tel quel de `student/sam-sessions/hooks/` : seuls les chemins et des types changent. Un
 * appelant qui tient lui-même son échec hors ligne passe `offlineDialog: false`.
 */
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../data/redux/actions/api"
import { samService } from "../../../data/services/samService"
import { requestOptionsFor } from "../../shared/apiCall"

const STALE_AFTER_MS = 5 * 60 * 1000 // 5 minutes (cf §5 sam-context cache)

/** Le contexte de Sam pour une activité, tel que le réducteur `sam` le garde. */
export type SamContext = {
    fetchedAt?: number
    source_kind?: string
    sequence?: { next_activity_id?: number | null } | null
} & Record<string, unknown>

type SamState = {
    data?: {
        sam?: {
            contextByActivityId?: Record<string, SamContext>
            contextErrorByActivityId?: Record<string, boolean>
        }
    }
    api?: Record<string, { request?: boolean } | undefined>
}

export function useSamContext(activityId: number | null, options: { offlineDialog?: boolean } = {}) {
    const offlineDialog = options.offlineDialog ?? true
    const dispatch = useDispatch()
    const context = useSelector((state: SamState) =>
        activityId != null ? state.data?.sam?.contextByActivityId?.[activityId] : undefined
    )
    const isLoading = useSelector((state: SamState) => !!state.api?.[samService.getSamContext.name]?.request)
    // Defensive read — the error map may be absent on a pre-flag hydrated
    // sam slice. True when the last context fetch for this activity failed
    // (404/500), so the screen can degrade to the standard activity flow.
    const hasError = useSelector(
        (state: SamState) => activityId != null && !!state.data?.sam?.contextErrorByActivityId?.[activityId] && !context
    )

    useEffect(() => {
        if (!activityId) return
        const fresh = context?.fetchedAt && Date.now() - context.fetchedAt < STALE_AFTER_MS
        if (fresh) return
        dispatch(
            apiRequest(
                samService.getSamContext,
                { id: activityId },
                {},
                {},
                null,
                null,
                requestOptionsFor(offlineDialog)
            ) as never
        )
    }, [activityId, context?.fetchedAt, dispatch, offlineDialog])

    return { context, isLoading, hasError }
}
